/**
 * Kategori satis anlatimi — Kesfet'te bir katalog secildiginde cizilir.
 *
 * Icerik yonetim panelinden gelir (bkz. schema.categoryContent); burada TEK
 * BIR metin sabit degil. Bolumler bos ise cizilmez: hazirlanma asamasindaki
 * bir kategori tek paragrafla, olgun bir kategori tam anlatimla yasar.
 *
 * ── FIYAT BASILMAZ ───────────────────────────────────────────────────────────
 * `showPrices` acilana kadar hicbir tutar gosterilmez — "fiyat icin teklif
 * alin" bile yazilmaz, cunku o cumle de bir fiyat vaadidir. Tek eylem
 * cagrisi: Teklif Al. (docs/WEB_HANDOFF_Satis_Modeli.md, kural 1 ve 6.)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CategorySales } from '@shared/index';
import { copy, useI18n } from '@/lib/i18n';
import { quoteRequestPath } from '@/lib/quote';
import { canonicalCatalogName } from '@/lib/format';
import './SalesSections.css';

interface SalesSectionsProps {
  sales: CategorySales;
  /**
   * Kategori tonu ('violet' | 'cyan' | ...). `pcard__cover--X` sinifi yalnizca
   * `--pcard-hue` degiskenini kurar, baska stil tasimaz — bu yuzden burada
   * yeniden kullanmak guvenli ve kategori rengi Kesfet ile ayni kalir.
   */
  hue?: string;
  category: string;
}

export function SalesSections({ sales, hue, category }: SalesSectionsProps) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const cta = sales.hero?.ctaLabel?.trim() || c('Teklif Al', 'Request a Quote');
  const ctaHref = quoteRequestPath({ solution: category, catalog: category, source: '/kesfet' });
  // Leds Play'in tek paket teklifi katalog başlığındadır; içerik bazlı talep açılmaz.
  const individualQuotes = canonicalCatalogName(category) !== 'Leds Play';

  const hasAnything =
    sales.hero ||
    sales.steps?.length ||
    sales.modules?.length ||
    sales.credits?.bullets.length ||
    sales.packages?.length ||
    sales.timeline?.steps.length ||
    sales.extras?.length ||
    sales.faq?.length;

  if (!hasAnything) return null;

  return (
    <div className={`sales ${hue ? `pcard__cover--${hue}` : ''}`}>
      {sales.hero?.title ? (
        <section className="sales__hero">
          <h2 className="sales__herotitle">{sales.hero.title}</h2>
          {sales.hero.subtitle ? <p className="sales__herosub">{sales.hero.subtitle}</p> : null}
          {individualQuotes && <Link className="sales__cta" to={ctaHref}>
            {cta}
          </Link>}
        </section>
      ) : null}

      {sales.steps?.length ? (
        <Block label={c('Nasıl çalışır', 'How it works')}>
          <ol className="sales__steps">
            {sales.steps.map((s, i) => (
              <li key={i} className="sales__step">
                <span className="sales__stepnum">{String(i + 1).padStart(2, '0')}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </Block>
      ) : null}

      {sales.modules?.length ? (
        <Block label={c('Neler dahil', "What's included")}>
          <div className="sales__modules">
            {sales.modules.map((m, i) => (
              <article key={i} className="sales__module">
                <h3>
                  {m.name}
                  {m.extra ? <span className="sales__tag">{c('Ek hizmet', 'Additional service')}</span> : null}
                </h3>
                <p>{m.text}</p>
              </article>
            ))}
          </div>
        </Block>
      ) : null}

      {sales.packages?.length ? (
        <Block label={c('Paketler', 'Packages')}>
          <div className="sales__packages">
            {sales.packages.map((p) => (
              <article
                key={p.id}
                className={`sales__pkg ${p.featured ? 'is-featured' : ''}`}
              >
                {p.featured ? <span className="sales__pkgflag">{c('En çok tercih edilen', 'Most selected')}</span> : null}
                <h3 className="sales__pkgname">{p.name}</h3>
                {p.intensity ? <p className="sales__pkgintensity">{p.intensity}</p> : null}

                <dl className="sales__pkgfeatures">
                  {p.features.map((f, i) => (
                    <div key={i}>
                      <dt>{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>

                {/* Fiyat satiri YOK — bilerek. Tek eylem: teklif. */}
                {individualQuotes && <Link className="sales__pkgcta" to={quoteRequestPath({ solution: `${category} — ${p.name}`, catalog: category, source: '/kesfet' })}>
                  {p.ctaLabel?.trim() || cta}
                </Link>}
              </article>
            ))}
          </div>
        </Block>
      ) : null}

      {sales.credits?.bullets.length ? (
        <Block label={c('Kredi kullanımı', 'Credit usage')}>
          <div className="sales__credits">
            {sales.credits.intro ? <p className="sales__creditintro">{sales.credits.intro}</p> : null}
            <ul className="sales__creditlist">
              {sales.credits.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        </Block>
      ) : null}

      {sales.timeline?.steps.length ? (
        <Block label={c('Kurulum', 'Installation')}>
          {sales.timeline.title ? <p className="sales__timelinetitle">{sales.timeline.title}</p> : null}
          <ol className="sales__timeline">
            {sales.timeline.steps.map((s, i) => (
              <li key={i}>
                <span className="sales__tlnum">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
          {sales.timeline.expectations?.length ? (
            <div className="sales__expect">
              <span className="sales__expecttitle">{c('Sizden beklenenler', 'What we need from you')}</span>
              <ul>
                {sales.timeline.expectations.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </Block>
      ) : null}

      {sales.extras?.length ? (
        <Block label={c('Ek hizmetler', 'Additional services')}>
          <ul className="sales__extras">
            {sales.extras.map((e, i) => (
              <li key={i}>
                <span>{e}</span>
                {individualQuotes && <Link to={quoteRequestPath({ solution: `${category} — ${e}`, catalog: category, source: '/kesfet' })}>{cta}</Link>}
              </li>
            ))}
          </ul>
        </Block>
      ) : null}

      {sales.faq?.length ? (
        <Block label={c('Sıkça sorulanlar', 'Frequently asked questions')}>
          <div className="sales__faq">
            {sales.faq.map((f, i) => (
              <Faq key={i} question={f.q} answer={f.a} />
            ))}
          </div>
        </Block>
      ) : null}
    </div>
  );
}

/** Bolum sarmalayici — uppercase mikro etiket + icerik (wondeon.com dili). */
function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="sales__block">
      <span className="sales__label">{label}</span>
      {children}
    </section>
  );
}

/**
 * Acilir soru.
 *
 * `<details>` degil kendi durumu: `<details>` acilirken yukseklik gecisi
 * yapmiyor ve tarayicilar arasi ok isareti farkli ciziliyor. Davranis basit,
 * kontrol bizde olsun.
 */
function Faq({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sales__q ${open ? 'is-open' : ''}`}>
      <button type="button" className="sales__qbtn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{question}</span>
        <span className="sales__qmark" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? <p className="sales__qa">{answer}</p> : null}
    </div>
  );
}
